import { useEffect, useMemo, useState } from "react";
import { fetchAllDCREntriesAdmin, deleteAnyDCREntry } from "../../api/dcrService";
import styles from "./DCRReports.module.css";

function formatTo12Hour(value24) {
  if (!value24) return "";
  const [hours, minutes] = value24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
}

function formatDate(value) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
}

function getTodayDate() {
  return new Date().toISOString().split("T")[0];
}

function truncateNote(value, maxLength = 60) {
  if (!value) return "";
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength).trim() + "...";
}

export default function DCRReports() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedNote, setSelectedNote] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadRecords = async () => {
      try {
        const data = await fetchAllDCREntriesAdmin();
        if (isMounted) setRecords(data);
      } catch (err) {
        console.error("DCR load error:", err);
        if (isMounted) setError("Failed to load DCR reports.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadRecords();
    return () => {
      isMounted = false;
    };
  }, []);

  const groupedRecords = useMemo(() => {
    const groups = {};
    records.forEach((record) => {
      const key = record.submittedBy || record.userId || "Unknown";
      if (!groups[key]) {
        groups[key] = { email: key, userId: record.userId, entries: [] };
      }
      groups[key].entries.push(record);
    });
    return Object.values(groups).sort((a, b) => a.email.localeCompare(b.email));
  }, [records]);

  const filteredGroupedRecords = useMemo(() => {
    if (!selectedDate) return groupedRecords;

    return groupedRecords
      .map((group) => ({
        ...group,
        entries: group.entries.filter((record) => record.date === selectedDate),
      }))
      .filter((group) => group.entries.length > 0);
  }, [groupedRecords, selectedDate]);

  const filteredSummary = useMemo(() => {
    const totalEntries = filteredGroupedRecords.reduce((sum, group) => sum + group.entries.length, 0);
    const totalEmployees = filteredGroupedRecords.length;
    return { totalEntries, totalEmployees };
  }, [filteredGroupedRecords]);

  const handleDelete = async (record) => {
    if (!record.userId || !record.id) return;
    const confirmed = window.confirm("Are you sure you want to delete this DCR entry?");
    if (!confirmed) return;

    try {
      await deleteAnyDCREntry(record.userId, record.id);
      setRecords((prev) => prev.filter((r) => r.id !== record.id || r.userId !== record.userId));
    } catch (err) {
      setError("Failed to delete DCR entry.");
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading DCR reports...</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>DCR Reports</h1>
          <p>View and manage daily call reports submitted by all employees.</p>
        </div>
        <div className={styles.summary}>
          <strong>{filteredSummary.totalEntries}</strong> entries from{" "}
          <strong>{filteredSummary.totalEmployees}</strong> employees
          {selectedDate && <span className={styles.filterTag}> for {selectedDate}</span>}
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.dateField}>
          <label htmlFor="dcr-date">Select Date</label>
          <input
            id="dcr-date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className={styles.dateInput}
          />
        </div>
        <div className={styles.filterActions}>
          <button
            type="button"
            className={styles.todayBtn}
            onClick={() => setSelectedDate(getTodayDate())}
          >
            Today
          </button>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => setSelectedDate("")}
            disabled={!selectedDate}
          >
            Clear
          </button>
        </div>
      </div>

      {error && <div className={styles.alertError}>{error}</div>}

      {filteredGroupedRecords.length === 0 ? (
        <div className={styles.empty}>
          {selectedDate
            ? `No DCR entries found for ${selectedDate}.`
            : "No DCR entries found."}
        </div>
      ) : (
        filteredGroupedRecords.map((group) => (
          <div key={group.userId} className={styles.groupCard}>
            <div className={styles.groupHeader}>
              <div>
                <h2>{group.email}</h2>
                <p>
                  {group.entries.length} entry{group.entries.length === 1 ? "" : "ies"}
                  {selectedDate && <span> on {selectedDate}</span>}
                </p>
              </div>
            </div>

            <div className={styles.scrollHint}>Scroll horizontally to view all columns</div>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>City</th>
                    <th>Area</th>
                    <th>Speciality</th>
                    <th>Doctor</th>
                    <th>Product</th>
                    <th>Visit Type</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Submitted At</th>
                    <th className={styles.notesColumn}>Notes</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {group.entries.map((record) => (
                    <tr key={record.id}>
                      <td>{record.city}</td>
                      <td>{record.area}</td>
                      <td>{record.speciality}</td>
                      <td>{record.doctor}</td>
                      <td>{Array.isArray(record.product) ? record.product.join(", ") : record.product}</td>
                      <td>{record.visitType}</td>
                      <td>{formatDate(record.date)}</td>
                      <td>{record.time ? formatTo12Hour(record.time) : "—"}</td>
                      <td>
                        <span className={`${styles.badge} ${styles[record.status?.toLowerCase()] || ""}`}>
                          {record.status}
                        </span>
                      </td>
                      <td>{record.submittedAt ? new Date(record.submittedAt).toLocaleString("en-IN") : "—"}</td>
                      <td className={styles.notesColumn}>
                        <div className={styles.notePreview} title={record.notes || ""}>
                          {truncateNote(record.notes, 50)}
                        </div>
                        {record.notes && record.notes.length > 50 && (
                          <button
                            type="button"
                            className={styles.viewNoteBtn}
                            onClick={() => setSelectedNote(record.notes)}
                          >
                            View
                          </button>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.deleteBtn}
                          onClick={() => handleDelete(record)}
                          title="Delete entry"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {selectedNote && (
        <div className={styles.modalOverlay} onClick={() => setSelectedNote(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Notes</h3>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setSelectedNote(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalNoteText}>{selectedNote}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
